export type Key<T, V> = {
    [K in keyof T]: T[K] extends V ? K : never;
}[keyof T];

export function stringCmp<T>(keySelector: ((item: T) => string) | Key<T, string>, desc: boolean = false, options?: Intl.CollatorOptions): (a: T, b: T) => number {
    if (typeof keySelector === 'function') {
        return (a: T, b: T) => {
            const cmp = keySelector(a).localeCompare(keySelector(b), undefined, options);
            return desc ? -cmp : cmp;
        };
    } else {
        return stringCmp(it => it[keySelector] as string, desc, options);
    }
}

export const naturalCmpOptions: Intl.CollatorOptions = {
    numeric: true,
    sensitivity: 'base',
};

export function numberCmp<T>(keySelector: ((item: T) => number) | Key<T, number>, desc: boolean = false): (a: T, b: T) => number {
    if (typeof keySelector === 'function') {
        return (a: T, b: T) => {
            const cmp = keySelector(a) - keySelector(b);
            return desc ? -cmp : cmp;
        };
    } else {
        return numberCmp(it => it[keySelector] as number, desc);
    }
}

export function maxBy<T>(input: readonly T[], cmp: (a: T, b: T) => number): T | undefined {
    if (input.length === 0) {
        return undefined;
    } else {
        return input.reduce((acc, item) => cmp(acc, item) > 0 ? acc : item);
    }
}

export function minBy<T>(input: readonly T[], cmp: (a: T, b: T) => number): T | undefined {
    if (input.length === 0) {
        return undefined;
    } else {
        return input.reduce((acc, item) => cmp(acc, item) < 0 ? acc : item);
    }
}

export function combineCmp<T>(...cmps: ((a: T, b: T) => number)[]): (a: T, b: T) => number {
    return (a: T, b: T) => cmps.reduce((acc, cmp) => acc || cmp(a, b), 0);
}
